import { Injectable } from '@angular/core';
import { FormArray, FormControl } from '@ngneat/reactive-forms';
import { ValidationService } from '@vinnova/validation';
import {
  BehaviorSubject,
  Observable,
  Subject,
  debounceTime,
  map,
  tap,
} from 'rxjs';
import { RendererSectionsQuery } from '../queries/renderer-sections.query';
import { filterNil } from '@ngneat/elf';

@Injectable({ providedIn: 'root' })
export class FormValidationService {
  private _sections = new Map<string, BehaviorSubject<void>>();
  private _showErrors = new Subject<boolean>();

  private _renderAllSections = new BehaviorSubject<boolean>(false);
  renderAllSections$ = this._renderAllSections.asObservable();

  showErrors$ = this._showErrors.asObservable();

  constructor(
    private validationService: ValidationService,
    private rendererSectionsQuery: RendererSectionsQuery,
  ) {}

  sectionInvalid(sectionId: string): Observable<boolean> {
    const sub = this.getOrCreateSub(sectionId);
    return sub.pipe(
      tap(() => this._renderAllSections.next(true)),
      debounceTime(0),
      map(() => {
        const section = document.querySelector(`#accordion-${sectionId}`);
        if (!section) {
          return false;
        }
        return !!section.querySelector('.invalid-feedback');
      }),
      debounceTime(0),
      tap(() => {
        this._renderAllSections.next(false);
      }),
    );
  }

  triggerSectionInvalidCheckAll() {
    for (const section of this._sections.values()) {
      section.next();
    }
  }

  triggerSectionInvalidCheck(questionId: string) {
    const section =
      this.rendererSectionsQuery.getSectionWithQuestionId(questionId);
    if (!section) {
      return;
    }
    const sub = this.getOrCreateSub(section.id);
    sub.next();
  }

  questionInvalid(
    control: FormControl<unknown> | FormArray<unknown>,
    questionId: string,
  ) {
    const section =
      this.rendererSectionsQuery.getSectionWithQuestionId(questionId);
    return this.validationService.isInvalid(control).pipe(
      filterNil(),
      tap(() => {
        if (!section) {
          return;
        }
        const sub = this.getOrCreateSub(section?.id);
        sub.next();
      }),
    );
  }

  showErrors() {
    this._showErrors.next(true);
    for (const [, value] of this._sections) {
      value.next();
    }
  }

  hideErrors() {
    this._showErrors.next(false);
  }

  private getOrCreateSub(sectionId): BehaviorSubject<void> {
    if (!this._sections.has(sectionId)) {
      this._sections.set(sectionId, new BehaviorSubject<void>(undefined));
    }

    return this._sections.get(sectionId);
  }
}
